import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { DealerServiceToken } from '../../factories/dealer.factory';
import { WorkerRead } from '../../models/communication/app-events.model';
import { DealerService } from '../../services/dealer.service';

@Component({
    selector: 'app-register-worker',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './register-worker.component.html',
    styleUrls: ['./register-worker.component.scss']
})
export class RegisterWorkerComponent implements OnInit, OnChanges {
    @Output() readonly closeClicked = new EventEmitter<void>();
    @Output() readonly backClicked = new EventEmitter<void>();
    @Output() readonly cancelSupervisorAction = new EventEmitter<void>();
    @Output() readonly workerCardScanned = new EventEmitter<void>();

    @Input() workerData: WorkerRead | null = null;
    @Input() tableGroups: string[] = [];

    step = 1;

    // Local editable state
    userId: string | null = null;
    user: { name: string; role: string; tableGroups: string[] } = {
        name: '',
        role: '',
        tableGroups: []
    };

    tableGroupSelection: Record<string, boolean> = {};
    roles: string[] = [$localize`Supervisor`, $localize`Croupier`];

    tableGroupDropdownOpen = false;
    roleDropdownOpen = false;

    constructor(@Inject(DealerServiceToken) private readonly dealerService: DealerService) {}

    get tableGroupDisplay(): string {
        if (this.user.tableGroups.length === 0) {
            return 'TABLE GROUP';
        }
        if (this.user.tableGroups.length === 1) {
            return this.user.tableGroups[0];
        }
        return `${this.user.tableGroups.length} groups selected`;
    }

    get isFormValid(): boolean {
        return !!this.user.name && this.user.tableGroups.length > 0 && !!this.user.role;
    }

    ngOnInit(): void {
        this.updateTableGroupSelection();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('workerData' in changes && this.workerData) {
            this.userId = this.workerData.id;
            this.user = {
                name: this.workerData.name ?? '',
                role: this.workerData.role ?? '',
                tableGroups: this.workerData.tableGroups ?? []
            };

            this.updateTableGroupSelection();
        }
    }

    onCardScanned(): void {
        this.step = 2;
    }

    goBack(): void {
        if (this.step > 1) {
            this.step--;
        }
        this.backClicked.emit();
    }

    cardScanned(): void {
        this.step = 2;
        this.workerCardScanned.emit();
    }

    editUser(): void {
        this.step = 2;
    }

    async submitForm(): Promise<void> {
        if (!this.userId || !this.user.name || this.user.tableGroups.length === 0 || !this.user.role) {
            return;
        }

        try {
            const response = await this.dealerService.sendRegisterWorkerRequest({
                id: this.userId,
                name: this.user.name,
                tableGroups: this.user.tableGroups,
                role: this.user.role
            });

            if (response.error) {
                this.step = 1;
            } else {
                this.step = 3;
            }
        } catch {
            // Handle network or other errors
            // this.errorOccurred.emit('Failed to register worker');
        }
    }

    toggleTableGroupDropdown(): void {
        this.tableGroupDropdownOpen = !this.tableGroupDropdownOpen;
        if (this.tableGroupDropdownOpen) {
            this.roleDropdownOpen = false;
        }
    }

    toggleRoleDropdown(): void {
        this.roleDropdownOpen = !this.roleDropdownOpen;
        if (this.roleDropdownOpen) {
            this.tableGroupDropdownOpen = false;
        }
    }

    toggleTableGroup(group: string): void {
        const index = this.user.tableGroups.indexOf(group);
        if (index > -1) {
            this.user.tableGroups.splice(index, 1);
            this.tableGroupSelection[group] = false;
        } else {
            this.user.tableGroups.push(group);
            this.tableGroupSelection[group] = true;
        }
    }

    selectRole(role: string): void {
        this.user.role = role;
        this.roleDropdownOpen = false;
    }

    trackByGroup = (_: number, group: string): string => group;
    trackByRole = (_: number, role: string): string => role;

    // Private methods
    private updateTableGroupSelection(): void {
        this.tableGroupSelection = {};
        this.tableGroups.forEach((group: string) => {
            this.tableGroupSelection[group] = this.user.tableGroups.includes(group);
        });
    }
}
